import { useState } from 'react';
import {
  ArrayField,
  Datagrid,
  DateField,
  FunctionField,
  TextField,
  useGetRecordId,
  useRedirect,
} from 'react-admin';
import { Link } from 'react-router-dom';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import PetsIcon from '@mui/icons-material/Pets';
import { Chip, CircularProgress, IconButton, Typography } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  CLAIM_REIMBURSEMENT_STATUS_COLOR,
  useClaimById,
  useClaimReimbursement,
} from '@pumpkincare/claims';
import {
  ClaimCustomerInformation,
  ClaimDetailCard,
  GENDER_MAP,
  SPECIES_MAP,
} from '@pumpkincare/shared';
import { useCareCreditDetails, useCustomer, usePet } from '@pumpkincare/users';

import { REIMBURSEMENT_MODES_WITHOUT_CHECKS } from '../../../constants/reimbursementMethods';
import useBooleanInput from '../../../hooks/useBooleanInput';
import { formatCurrency } from '../../../shared/utils';
import { appendBreedName } from '../../../shared/utils/petUtils';
import CancellationConfirmModal from '../cancellation-confirm-modal';
import ReimbursementActionButton from '../reimbursement-action-button';
import ReimbursementCancellationDetail from '../reimbursement-cancellation-detail';
import ReimbursementCancellationModal from '../reimbursement-cancellation-modal';
import ReissueReimbursementModal from '../reissue-reimbursement-modal';
import { reimbursementDetailStyle } from './reimbursement-detail-style';

const REIMBURSEMENT_MAP = {
  insurance: 'Insurance',
  prevent: 'Preventive Essentials',
  wellness: 'Pumpkin Wellness',
};

export default function ReimbursementDetail() {
  const { fer5532PumpkinCarecreditReimbursements } = useFlags();
  const reimbursementMethodsWithoutChecks = fer5532PumpkinCarecreditReimbursements
    ? REIMBURSEMENT_MODES_WITHOUT_CHECKS
    : ['ACH', 'RTP'];
  const redirect = useRedirect();
  const classes = reimbursementDetailStyle();
  const claimId = useGetRecordId();

  const [isCancellationModalOpen, toggleCancellationModal] = useBooleanInput();
  const [isConfirmCancellationModalOpen, toggleConfirmCancellationModal] =
    useBooleanInput();
  const [isReissueReimbursementOpen, toggleReissueReimbursementModal] =
    useBooleanInput();
  const [reimbursement, setReimbursement] = useState();

  const { data: claimData } = useClaimById(claimId);
  const { data: reimbursementData = [], isLoading: isReimbursementDataLoading } =
    useClaimReimbursement(claimId);
  const { data: petData, isLoading: isPetDataLoading } = usePet(claimData?.pet_id);
  const { data: customerData, isLoading: isCustomerLoading } = useCustomer(
    claimData?.customer_id
  );

  const shouldFetchCareCreditDetails =
    fer5532PumpkinCarecreditReimbursements &&
    !isReimbursementDataLoading &&
    reimbursementData?.some(
      reimbursement => reimbursement.reimbursement_method === 'CARE_CREDIT'
    );
  const { data: careCreditData, isLoading: isCareCreditDataLoading } =
    useCareCreditDetails(claimData?.customer_id, shouldFetchCareCreditDetails);

  const mailingAddress = customerData ? customerData.mailing_address : {};

  function handleCancelClick(reimbursementClicked) {
    setReimbursement(reimbursementClicked);
    toggleCancellationModal();
  }

  function handleConfirmClick(reimbursementClicked) {
    setReimbursement(reimbursementClicked);
    toggleConfirmCancellationModal();
  }

  function handleReissueClick(reimbursementClicked) {
    setReimbursement(reimbursementClicked);
    toggleReissueReimbursementModal();
  }

  function renderReimbursementMethod(record) {
    let reimbursementMethod = record.reimbursement_method;
    if (reimbursementMethodsWithoutChecks.includes(reimbursementMethod)) {
      if (reimbursementMethod === 'CARE_CREDIT') {
        return (
          <Typography className={classes.reimbursementCell}>
            {careCreditData?.account_number
              ? `CareCredit ending in ${careCreditData.account_number}`
              : 'CareCredit'}
          </Typography>
        );
      }
      return (
        <Typography className={classes.reimbursementCell}>
          {`${reimbursementMethod} ${
            record.funding_source
              ? `ending in ${record.funding_source.account_number}`
              : ''
          }`}
        </Typography>
      );
    }

    return (
      <Typography className={classes.reimbursementCell}>
        {`Physical Check - ${record.lob_check_number}`}
      </Typography>
    );
  }

  return (
    <div className={classes.mainDiv}>
      <Link to={`/claims/${claimId}/show`} className={classes.link}>
        <ArrowBackIosIcon className={classes.arrowBackIcon} />
        <Typography
          className={classes.claimNumber}
        >{`RETURN TO CLAIM ${claimId}`}</Typography>
      </Link>
      <div className={classes.headerWrapper}>
        {isCustomerLoading ? (
          <CircularProgress />
        ) : (
          <ClaimCustomerInformation customerData={customerData} />
        )}

        {!isPetDataLoading && petData ? (
          <ClaimDetailCard
            icon={
              <IconButton
                onClick={() => redirect(`/pets/${petData.id}/show`)}
                style={{ padding: '0px' }}
                size='large'
              >
                <PetsIcon
                  style={{ width: '35px', height: '35px', color: '#007DFF' }}
                />
              </IconButton>
            }
            title={`${petData.name}`}
            detail={`${SPECIES_MAP[petData.species]}, ${
              appendBreedName(petData, 'breed')?.breed
            }, Age ${petData?.age}, ${GENDER_MAP[petData?.gender] || 'N/A'}`}
          />
        ) : (
          <CircularProgress />
        )}
      </div>
      <ArrayField
        source='reimbursements'
        record={{
          reimbursements: reimbursementData,
        }}
      >
        <Datagrid
          classes={{
            headerCell: classes.headerCell,
            table: classes.table,
          }}
          rowSx={() => ({
            backgroundColor: 'white',
          })}
          bulkActionButtons={false}
          isRowExpandable={record => record.cancellations?.length > 0}
          expand={<ReimbursementCancellationDetail />}
          rowClick={false}
        >
          <FunctionField
            label='STATUS'
            render={(record = {}) => (
              <Chip
                style={{
                  fontSize: '10px',
                  height: '20px',
                  color: '#000000',
                  backgroundColor:
                    CLAIM_REIMBURSEMENT_STATUS_COLOR[record.status] || '#FFFFFF',
                  display: 'inline-flex',
                }}
                label={record.status?.toUpperCase()}
              />
            )}
          />

          <FunctionField
            label='TYPE'
            render={(record = {}) => (
              <Typography className={classes.reimbursementCell}>
                {REIMBURSEMENT_MAP[record.reimbursement_type]}
              </Typography>
            )}
          />

          <FunctionField
            label='AMOUNT PAID'
            render={(record = {}) => (
              <Typography className={classes.reimbursementCell}>
                {formatCurrency(record.total_reimbursement_amount, {
                  areCents: true,
                })}
              </Typography>
            )}
          />
          <FunctionField
            label='METHOD'
            render={(record = {}) =>
              !isCareCreditDataLoading ? (
                renderReimbursementMethod(record)
              ) : (
                <CircularProgress />
              )
            }
          />
          <FunctionField
            label='ID'
            render={(record = {}) => (
              <Typography className={classes.reimbursementCell}>
                {record.reimbursement_method === 'ACH'
                  ? record.ach_trace_id
                  : record.check_id || record.whitelabel_e_check_id}
              </Typography>
            )}
          />
          <TextField
            className={classes.reimbursementCell}
            source='dwolla_external_id'
            label='DWOLLA TRANSACTION ID'
          />
          {fer5532PumpkinCarecreditReimbursements ? (
            <TextField
              className={classes.reimbursementCell}
              source='care_credit_payment_id'
              label='CARECREDIT PAYMENT ID'
            />
          ) : null}
          <DateField
            className={classes.reimbursementCell}
            label='CREATED'
            source='created_at'
          />
          <FunctionField
            label='CANCEL DATE'
            render={(record = {}) => (
              <DateField
                className={classes.reimbursementCell}
                record={{
                  cancellation_date: record.cancellations?.find(
                    cancellation => cancellation.is_approved === true
                  )?.decision_made_at,
                }}
                source='cancellation_date'
              />
            )}
          />
          <FunctionField
            label=''
            render={(record = {}) => (
              <ReimbursementActionButton
                reimbursement={record}
                claimStatus={claimData?.status}
                onConfirmCancellationClick={handleConfirmClick}
                onReissueClick={handleReissueClick}
                onRequestCancellationClick={handleCancelClick}
              />
            )}
          />
        </Datagrid>
      </ArrayField>
      <ReimbursementCancellationModal
        reimbursement={reimbursement}
        isOpen={isCancellationModalOpen}
        onClose={toggleCancellationModal}
      />
      <CancellationConfirmModal
        reimbursement={reimbursement}
        isOpen={isConfirmCancellationModalOpen}
        onClose={toggleConfirmCancellationModal}
      />
      <ReissueReimbursementModal
        reimbursement={reimbursement}
        isOpen={isReissueReimbursementOpen}
        onClose={toggleReissueReimbursementModal}
        mailingAddress={mailingAddress}
        claimId={claimId}
      />
    </div>
  );
}
